/**
 * Contact Form - Support ticket submission
 */

import React, { useState } from 'react'
import {
  Box,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Paper,
  Stack,
  CircularProgress,
  FormHelperText
} from '@mui/material'
import {
  Send as SendIcon,
  AttachFile as AttachIcon,
  CheckCircle as SuccessIcon
} from '@mui/icons-material'
import { apiClient } from '../../../services/api'

interface ContactFormProps {
  fpCode: string
}

interface FormData {
  name: string
  email: string
  phone: string
  category: string
  priority: string
  subject: string
  description: string
}

const ContactForm: React.FC<ContactFormProps> = ({ fpCode }) => {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    phone: '',
    category: '',
    priority: 'medium',
    subject: '',
    description: ''
  })
  const [errors, setErrors] = useState<Partial<FormData>>({})
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const categories = [
    'Sync Issues',
    'Data Exceptions',
    'Access Problems',
    'Feature Request',
    'Bug Report',
    'General Question',
    'Other'
  ]

  const priorities = [
    { value: 'low', label: 'Low - General question or feature request' },
    { value: 'medium', label: 'Medium - Non-critical issue' },
    { value: 'high', label: 'High - Business impact' },
    { value: 'urgent', label: 'Urgent - Critical business impact' }
  ]

  const handleChange = (field: keyof FormData) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | { target: { value: string } }
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }))
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev }
        delete newErrors[field]
        return newErrors
      })
    }
  }

  const validateForm = (): boolean => {
    const newErrors: Partial<FormData> = {}
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required'
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email address'
    }
    
    if (!formData.category) {
      newErrors.category = 'Please select a category'
    }
    
    if (!formData.subject.trim()) {
      newErrors.subject = 'Subject is required'
    }
    
    if (!formData.description.trim()) {
      newErrors.description = 'Description is required'
    } else if (formData.description.length < 20) {
      newErrors.description = 'Please provide more details (minimum 20 characters)'
    }
    
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    
    if (!validateForm()) {
      return
    }
    
    try {
      setSubmitting(true)
      setSubmitError(null)
      
      // Submit to support API (Freshdesk integration)
      await apiClient.post('/api/support/ticket', {
        ...formData,
        fp_code: fpCode,
        source: 'inspector'
      })
      
      setSubmitted(true)
      // Reset form after successful submission
      setFormData({
        name: '',
        email: '',
        phone: '',
        category: '',
        priority: 'medium',
        subject: '',
        description: ''
      })
    } catch (err: any) {
      console.error('Error submitting support ticket:', err)
      setSubmitError(err.response?.data?.detail || 'Failed to submit support ticket. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'error'
      case 'high': return 'warning'
      case 'medium': return 'info'
      case 'low': return 'success'
      default: return 'default'
    }
  }

  if (submitted) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <SuccessIcon color="success" sx={{ fontSize: 60, mb: 2 }} />
        <Typography variant="h5" gutterBottom>
          Support Ticket Submitted
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          Thank you for contacting support. We'll respond to your request within 24 hours.
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          A confirmation email has been sent to {formData.email}
        </Typography>
        <Button variant="contained" onClick={() => setSubmitted(false)}>
          Submit Another Ticket
        </Button>
      </Paper>
    )
  }

  return (
    <Box>
      <Typography variant="h5" fontWeight={600} gutterBottom>
        Contact Support
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        Submit a support ticket and we'll get back to you as soon as possible.
      </Typography>

      {submitError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSubmitError(null)}>
          {submitError}
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        <form onSubmit={handleSubmit}>
          <Stack spacing={3}>
            {/* Contact Information */}
            <Typography variant="subtitle1" fontWeight={600}>
              Contact Information
            </Typography>
            
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                fullWidth
                label="Name"
                value={formData.name}
                onChange={handleChange('name')}
                error={!!errors.name}
                helperText={errors.name}
                required
              />
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={handleChange('email')}
                error={!!errors.email}
                helperText={errors.email}
                required
              />
            </Stack>
            
            <TextField
              fullWidth
              label="Phone (Optional)"
              value={formData.phone}
              onChange={handleChange('phone')}
              helperText="Include for urgent issues"
            />

            {/* Issue Details */}
            <Typography variant="subtitle1" fontWeight={600}>
              Issue Details
            </Typography>
            
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <FormControl fullWidth error={!!errors.category} required>
                <InputLabel>Category</InputLabel>
                <Select
                  value={formData.category}
                  label="Category"
                  onChange={(e) => handleChange('category')(e as any)}
                >
                  {categories.map(cat => (
                    <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                  ))}
                </Select>
                {errors.category && <FormHelperText>{errors.category}</FormHelperText>}
              </FormControl>
              
              <FormControl fullWidth required>
                <InputLabel>Priority</InputLabel>
                <Select
                  value={formData.priority}
                  label="Priority"
                  onChange={(e) => handleChange('priority')(e as any)}
                  color={getPriorityColor(formData.priority) as any}
                >
                  {priorities.map(p => (
                    <MenuItem key={p.value} value={p.value}>
                      <Stack>
                        <Typography variant="body2">{p.value.charAt(0).toUpperCase() + p.value.slice(1)}</Typography>
                        <Typography variant="caption" color="text.secondary">{p.label}</Typography>
                      </Stack>
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>Select based on business impact</FormHelperText>
              </FormControl>
            </Stack>
            
            <TextField
              fullWidth
              label="Subject"
              value={formData.subject}
              onChange={handleChange('subject')}
              error={!!errors.subject}
              helperText={errors.subject}
              required
            />
            
            <TextField
              fullWidth
              multiline
              rows={6}
              label="Description"
              value={formData.description}
              onChange={handleChange('description')}
              error={!!errors.description}
              helperText={errors.description || 'Please provide detailed information about your issue'}
              required
            />

            {/* Additional Info */}
            <Alert severity="info">
              <Typography variant="body2">
                <strong>Tips for faster resolution:</strong>
              </Typography>
              <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                <li>Include specific error messages</li>
                <li>Mention the affected franchise partner code: {fpCode}</li>
                <li>Describe steps to reproduce the issue</li>
                <li>Attach screenshots if applicable</li>
              </ul>
            </Alert>

            {/* Submit Button */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <Button
                variant="outlined"
                onClick={() => setFormData({
                  name: '',
                  email: '',
                  phone: '',
                  category: '',
                  priority: 'medium',
                  subject: '',
                  description: ''
                })}
                disabled={submitting}
              >
                Clear Form
              </Button>
              <Button
                type="submit"
                variant="contained"
                startIcon={submitting ? <CircularProgress size={20} /> : <SendIcon />}
                disabled={submitting}
              >
                {submitting ? 'Submitting...' : 'Submit Ticket'}
              </Button>
            </Box>
          </Stack>
        </form>
      </Paper>
    </Box>
  )
}

export default ContactForm